<script lang="ts" setup>
import {
  Project,
  CreateProjectRequest,
  UpdateProjectDetailsRequest,
} from '@beeboard/common';
import { getFirstDeadlineOfProject } from '@beeboard/common';
import { omit } from 'lodash-es';
import { useCustomPropertiesStore } from '@/store/customPropertiesStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectMailStore } from '@/store/projectMailStore';
import { useProjectStore } from '@/store/projectStore';
import { useTemplateStore } from '@/store/templateStore';
import { diff } from '@/utils/helpers/diff';
import ProjectDataForm from './create/ProjectDataForm.vue';
import EditProjectCustomProperties from './EditProjectCustomProperties.vue';
import ProjectMailAliasField from './mail/ProjectMailAliasField.vue';
import ProjectNonWorkingDays from './ProjectNonWorkingDays.vue';

interface Props {
  projectId: string;
  manageResponsible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  manageResponsible: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const projectStore = useProjectStore();
const templateStore = useTemplateStore();
const profileStore = useProfileStore();
const customPropertiesStore = useCustomPropertiesStore();
const projectMailStore = useProjectMailStore();
const message = useAlerts();
const loading = ref(true);

// Without a mailbox ProjectMailAliasField renders nothing, so the labelled row has to go
// too. Fetched here because the child's own mount never happens once the row is gated.
const isProjectMailboxConfigured = computed(
  () => !!projectMailStore.status(props.projectId)?.mailbox_configured,
);

const hasCustomProperties = computed(
  () => customPropertiesStore.hasCustomProperties,
);

const blankFormData: CreateProjectRequest = {
  title: '',
  volume: null,
  number: null,
  address: null,
  deadlines: [''],
  start_date: '',
  responsible: '',
  description: null,
  scopes: [],
  parent_id: null,
  project_image_url: null,
  project_documents_path: null,
  calendar: [],
};

const isActive = ref(true);
const isCalendarSyncEnabled = ref(false);
const calendarSyncHintId = useId();

const formData = ref<CreateProjectRequest>(blankFormData);
const editCustomPropertiesDialogIsVisible = ref(false);

const project = computed(() =>
  projectStore.hasProject(props.projectId)
    ? projectStore.project(props.projectId)
    : null,
);

const template = computed(() => {
  if (
    !project.value?.template ||
    !templateStore.hasTemplate(project.value.template)
  ) {
    return { title: '' };
  }

  return templateStore.template(project.value.template);
});

const maxDate = computed(() => {
  if (!project.value) {
    return null;
  }
  const firstDeadline = getFirstDeadlineOfProject(project.value);
  if (!firstDeadline) {
    return null;
  }
  const firstDeadlineDate = new Date(firstDeadline);
  firstDeadlineDate.setUTCDate(firstDeadlineDate.getUTCDate() - 1);

  return firstDeadlineDate.toISOString();
});

onMounted(async () => {
  try {
    await Promise.all([
      loadUserProfile(),
      templateStore.loadList(),
      loadMembers(),
      loadProject(),
      customPropertiesStore.loadCompanyProjectProperties(),
      projectStore.loadProjectImage(props.projectId),
      projectMailStore.ensureAliasStatus(props.projectId),
    ]);
    isActive.value = !!project.value?.is_active;
    isCalendarSyncEnabled.value = !!project.value?.is_calendar_sync_enabled;
  } catch (error) {
    message.error(error);
  } finally {
    loading.value = false;
  }
});

function loadProject() {
  return projectStore
    .loadProject(props.projectId)
    .then((project) => {
      Object.assign(formData.value, toUpdateProjectRequest(project));
    })
    .catch(message.error);
}

function loadUserProfile() {
  return profileStore.loadProfile();
}

function loadMembers() {
  return projectStore.loadMembers(props.projectId);
}

function toUpdateProjectRequest(project: Project) {
  return {
    title: project.title,
    responsible: project.responsible?.id,
    volume: project.volume,
    number: project.number,
    address: project.address,
    description: project.description,
    scopes: project.scopes,
    parent_id: project.parent_id,
    start_date: project.start_date,
    template: project.template,
    project_image_url: project.project_image_url,
    project_documents_path: project.project_documents_path,
    calendar: project.calendar,
  } as CreateProjectRequest;
}

async function submit(
  formData: CreateProjectRequest,
  projectImage: File | null,
  deleteImage: boolean,
) {
  const project = projectStore.project(props.projectId);
  const originalProjectDetails = toUpdateProjectRequest(project);
  const updateProjectDetails = omit(formData, ['deadlines']);

  const updateData: UpdateProjectDetailsRequest = {
    ...diff(originalProjectDetails, updateProjectDetails),
  };

  loading.value = true;

  const shouldDeleteImage =
    deleteImage ||
    !!(formData.project_image_url && projectStore.imageUrls[props.projectId]);

  try {
    await projectStore.updateDetails(props.projectId, {
      ...updateData,
      file: projectImage ?? undefined,
      delete_image: shouldDeleteImage || undefined,
    });
    if (projectImage) {
      projectStore.loadProjectImage(props.projectId, true);
    }
    message.success();
    try {
      await projectStore.loadProjectsDetailsList();
    } catch (error) {
      message.error(error);
    }
  } catch (error) {
    message.error(error);
  } finally {
    loading.value = false;
    emit('close');
  }
}

function toggleIsActive(value: boolean) {
  loading.value = true;

  return projectStore
    .updateDetails(props.projectId, { is_active: value })
    .then(() => message.success())
    .catch(message.error)
    .finally(() => {
      loading.value = false;
    });
}

async function toggleCalendarSync(value: boolean) {
  loading.value = true;
  try {
    await projectStore.toggleCalendarSync(props.projectId, value);
    // Refresh the cached projects-details list so the projects-list view
    // doesn't show a stale `is_calendar_sync_enabled` until the next route
    // refresh — matches the post-save pattern used by `submit`.
    await projectStore.loadProjectsDetailsList();
    message.success();
  } catch (error) {
    isCalendarSyncEnabled.value = !value;
    const apiError = error as { body?: { code?: string; message?: string } };
    if (apiError?.body?.code === 'CalendarSyncNotConfigured') {
      message.error($t('project.calendarSync.notConfigured'));
    } else {
      message.error(error);
    }
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <UiOverlay v-model="editCustomPropertiesDialogIsVisible">
    <EditProjectCustomProperties
      :project-id="props.projectId"
      @close="editCustomPropertiesDialogIsVisible = false"
    />
  </UiOverlay>

  <UiModal title="" is-wide @close="emit('close')">
    <template #header>
      <div class="modal-header">
        <div class="title">
          {{ $t('project.editProject') }}
          <span v-if="template?.title">
            ({{ $t('project.fromTemplate', { template: template.title }) }})
          </span>
        </div>
        <div class="subtitle-box">
          <div class="subtitle">
            {{ $t('project.projectDetailsAndMandatory') }}
          </div>
          <div class="header-actions">
            <UiButton
              v-if="hasCustomProperties"
              variant="default"
              @click="editCustomPropertiesDialogIsVisible = true"
            >
              <UiIcon name="Settings" />
              {{ $t('customProperties.editProjectProperties') }}
            </UiButton>
            <div class="is-active-toggle">
              {{
                isActive ? $t('project.isActive') : $t('project.isNotActive')
              }}
              <UiSwitch
                v-model="isActive"
                :disabled="loading"
                @update:model-value="toggleIsActive"
              >
              </UiSwitch>
            </div>
          </div>
        </div>
        <UiButton
          variant="flat"
          class="close-icon-button"
          @click="emit('close')"
        >
          <UiIcon name="XIcon" class="close-icon" />
        </UiButton>
      </div>
    </template>

    <ProjectDataForm
      :form="formData"
      :loading="loading"
      :max-date="maxDate"
      :project-id="props.projectId"
      :manage-responsible="props.manageResponsible"
      is-edit
      @cancel="emit('close')"
      @next="submit"
    >
      <template #after-documents-path>
        <UiField
          v-if="isProjectMailboxConfigured"
          class="project-mail-field"
          :label="$t('projectMail.addressTitle')"
        >
          <ProjectMailAliasField :project-id="props.projectId" />
        </UiField>
      </template>

      <template #after-calendar>
        <div
          v-if="project?.is_calendar_sync_available"
          class="calendar-sync-row"
        >
          <span :id="calendarSyncHintId" class="calendar-sync-hint">
            {{
              isCalendarSyncEnabled
                ? $t('project.calendarSync.enabledHint')
                : $t('project.calendarSync.disabledHint')
            }}
          </span>
          <UiSwitch
            v-model="isCalendarSyncEnabled"
            :disabled="loading"
            :aria-label="$t('project.calendarSync.label')"
            :aria-describedby="calendarSyncHintId"
            @update:model-value="toggleCalendarSync"
          >
          </UiSwitch>
        </div>
        <ProjectNonWorkingDays
          :project-id="props.projectId"
          :disabled="loading"
        />
      </template>
    </ProjectDataForm>
  </UiModal>
</template>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding-bottom: var(--space-24);

  .title {
    display: flex;
    gap: var(--space-8);
    font-size: var(--font-size-20);
    line-height: var(--line-height-24);
    color: var(--color-gray-900);
    font-weight: var(--font-weight-medium);

    span {
      color: var(--color-gray-400);
    }
  }
  .subtitle-box {
    display: flex;
    justify-content: space-between;
    align-items: end;
    .subtitle {
      font-size: var(--font-size-14);
      color: var(--color-gray-500);
      font-weight: var(--font-weight-regular);
    }
    .header-actions {
      display: flex;
      gap: var(--space-16);
      align-items: center;
    }
    .is-active-toggle {
      display: flex;
      align-items: end;
      gap: var(--space-8);
      line-height: var(--line-height-20);
      font-weight: var(--font-weight-medium);
      color: var(--color-gray-700);
    }
  }
  .close-icon-button {
    position: absolute;
    right: 4px;
    top: 12px;
  }
}

.project-mail-field {
  flex: 1;
  min-width: 0;

  :deep(.ui-control) {
    display: flex;
    align-items: center;
    min-height: px(44);
  }
}

.calendar-sync-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-8);
  padding-bottom: var(--space-16);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  line-height: var(--line-height-20);

  .calendar-sync-hint {
    font-size: var(--font-size-14);
    color: var(--color-gray-500);
    font-weight: var(--font-weight-regular);
  }
}

.duo-input {
  display: flex;
  gap: var(--space-16);
  width: 100%;
}

.half-input {
  width: 49%;
}

:deep(.modal-main) {
  overflow-y: auto;
  @include focus-ring-room;
  @media (max-height: 1059px) {
    padding-right: var(--space-16);
    margin-right: 0;
  }
}

:deep(.ui-field) {
  width: 100%;
}
</style>
